import {
  CartUpdateEvent,
  GorgiasChatProgrammaticOpenEvent,
  KlaviyoFormProgrammaticOpenEvent,
  AliaPopupProgrammaticOpenEvent,
  AttentivePopupProgrammaticOpenEvent,
} from "@theme/events";
import {
  isClickedOutside,
  onAttentiveReady,
  onDocumentReady,
  onGorgiasReady,
  requestIdleCallback,
} from "@theme/utilities";
import {
  onCustomerIdentified,
  isIdentifiedCustomer,
} from "@theme/identified-customer-promo";

/**
 * Page builders (Instant, Replo) and rich text can only emit links, so these hashes stand in
 * for a theme CTA click action: `#alia-8115`, `#klaviyo-form-StTFvD`, `#attentive-123`,
 * `#gorgias`, `#gorgias-homepage`, `#gorgias-conversation`.
 *
 * @param {string} hash
 * @returns {{ type: "alia", campaignId: string } | { type: "klaviyo", formId: string } | { type: "attentive", creativeId: string } | { type: "gorgias", page?: string } | null}
 */
export function parseCtaAnchor(hash) {
  const value = String(hash || "").replace(/^#/, "");

  const alia = value.match(/^alia-(.+)$/i);
  if (alia) return { type: "alia", campaignId: alia[1] };

  const klaviyo = value.match(/^klaviyo-form-(.+)$/i);
  if (klaviyo) return { type: "klaviyo", formId: klaviyo[1] };

  const attentive = value.match(/^attentive-(.+)$/i);
  if (attentive) return { type: "attentive", creativeId: attentive[1] };

  const gorgias = value.match(/^gorgias(?:-(conversation|homepage))?$/i);
  if (gorgias) return { type: "gorgias", page: gorgias[1]?.toLowerCase() };

  return null;
}

/**
 * @param {ReturnType<typeof parseCtaAnchor>} action
 */
export function openCtaAnchor(action) {
  if (!action) return;

  if (action.type === "alia") {
    document.dispatchEvent(
      new AliaPopupProgrammaticOpenEvent(action.campaignId),
    );
    return;
  }

  if (action.type === "klaviyo") {
    document.dispatchEvent(new KlaviyoFormProgrammaticOpenEvent(action.formId));
    return;
  }

  if (action.type === "attentive") {
    document.dispatchEvent(
      new AttentivePopupProgrammaticOpenEvent(action.creativeId),
    );
    return;
  }

  if (action.type === "gorgias") {
    document.dispatchEvent(new GorgiasChatProgrammaticOpenEvent(action.page));
  }
}

/**
 * @param {MouseEvent} event
 */
export function handleCtaAnchorClick(event) {
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const path =
    typeof event.composedPath === "function" ? event.composedPath() : [];
  const fromPath = path.find((node) => node instanceof HTMLAnchorElement);
  const target =
    event.target instanceof Element ? event.target.closest("a") : null;
  const link = fromPath || target;

  if (!link?.hash || link.target === "_blank") return;

  const action = parseCtaAnchor(link.hash);
  if (!action) return;

  const url = new URL(link.href, window.location.href);
  const samePage =
    url.origin === window.location.origin &&
    url.pathname === window.location.pathname &&
    url.search === window.location.search;

  if (!samePage) return;

  event.preventDefault();
  openCtaAnchor(action);
}

onDocumentReady(() => {
  // Capture phase, so a page builder's own link handler calling `stopPropagation()` can't swallow the click
  document.addEventListener("click", handleCtaAnchorClick, { capture: true });

  // This will sync local theme cart counts with
  // changes made within the Upcart flyout drawer.
  // Avoid on cart page as the event repeatedly fires
  // indefinitely and page doesn't show the upcart drawer
  if (window.location.pathname !== "/cart") {
    window.addEventListener(
      "aftersell-upcart:public-events:cart-loaded",
      () => {
        window.queueMicrotask(() => {
          window.upcartSubscribeCartUpdated(({ cart }) => {
            const items = cart?.items || [];
            const itemCount = items.reduce(
              (accumulator, item) => accumulator + (item.quantity || 0),
              0,
            );

            document.dispatchEvent(
              new CartUpdateEvent(cart, "upcart", {
                source: "upcart:cart.change",
                itemCount,
              }),
            );
          });
        });
      },
      { once: true },
    );
  }

  // Auto-open the Gorgias chat widget if the provided open chat flag URL parameters are included in the URL
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const openChat = urlParams.get("open_chat");

  if (openChat === "true") {
    onGorgiasReady().then(() => {
      // Page can be one of "chat", "conversation", or "homepage" [default]
      const page = urlParams.get("chat_page");

      if (page) {
        window.GorgiasChat.setPage(page);
      }

      window.GorgiasChat.open();
    });
  }

  const openGorgiasChatEventHandler = (event) => {
    const { page, prefillMessage } = event?.detail || {};

    // @see https://docs.gorgias.com/en-US/subscribe-to-chat-events-460530 for adding more Gorgias events
    onGorgiasReady().then(() => {
      if (page) {
        // Page can be one of "chat", "conversation", or "homepage" [default]
        window.GorgiasChat.setPage(page);
      }

      if (prefillMessage) {
        window.GorgiasChat.setCurrentInput(prefillMessage);
      }

      window.GorgiasChat.open();
    });
  };

  const openKlaviyoFormEventHandler = (event) => {
    const { formId } = event?.detail?.data || event?.detail || {};

    if (formId) {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(["openForm", formId]);
    }
  };

  const openAliaPopupEventHandler = (event) => {
    const { campaignId } = event?.detail?.data || event?.detail || {};

    if (campaignId) {
      window.alia = window.alia || [];
      window.alia.push({
        type: "open",
        campaignID: campaignId,
        ignoreTargeting: true,
      });
    }
  };

  // Attentive has no stub queue to push onto before its tag loads, so wait for it with the
  // shared `onAttentiveReady` poll. Clicks while a trigger is pending only update the creative
  // to open, so a burst of clicks before the tag loads opens one sign-up unit: the last one asked for.
  // @see https://help.attentive.com/hc/en-us/articles/10913611055636 for the Attentive trigger API
  let pendingAttentiveCreativeId = null;

  const openAttentivePopupEventHandler = (event) => {
    const { creativeId } = event?.detail?.data || event?.detail || {};

    if (!creativeId) {
      return;
    }

    const isTriggerPending = pendingAttentiveCreativeId !== null;
    pendingAttentiveCreativeId = creativeId;

    if (isTriggerPending) {
      return;
    }

    onAttentiveReady().then((isReady) => {
      const latestCreativeId = pendingAttentiveCreativeId;
      pendingAttentiveCreativeId = null;

      if (isReady) {
        window.__attentive.trigger(null, null, null, latestCreativeId);
      }
    });
  };

  /**
   * Handles the custom event 'alia:signup' fired when a user signs up in the Alia pop-up.
   * Skips re-running identification if the visitor is already identified (from a previous
   * session/tab or another capture path); otherwise marks them identified.
   */
  const onAliaSignupEventHandler = () => {
    if (isIdentifiedCustomer()) {
      return;
    }

    onCustomerIdentified();
  };

  // Add a global custom event listener for the programmatic opening of the Gorgias chat widget.
  document.addEventListener(
    GorgiasChatProgrammaticOpenEvent.eventName,
    openGorgiasChatEventHandler,
  );

  // Add a global custom event listener for the programmatic opening of a Klaviyo form.
  document.addEventListener(
    KlaviyoFormProgrammaticOpenEvent.eventName,
    openKlaviyoFormEventHandler,
  );

  // Add a global custom event listener for the programmatic opening of an Alia popup.
  document.addEventListener(
    AliaPopupProgrammaticOpenEvent.eventName,
    openAliaPopupEventHandler,
  );

  // Add a global custom event listener for the programmatic opening of an Attentive popup.
  document.addEventListener(
    AttentivePopupProgrammaticOpenEvent.eventName,
    openAttentivePopupEventHandler,
  );

  openCtaAnchor(parseCtaAnchor(window.location.hash));

  document.addEventListener("alia:signup", () => {
    onAliaSignupEventHandler();
  });

  // Setup an event listener when the Gorgias chat widget is ready and open to allow closing
  // the open Gorgias chat widget if the user clicks outside the chat window's GUI
  onGorgiasReady().then(() => {
    /**
     * Handles clicks outside of the Gorgias chat widget container.
     *
     * When the chat widget is open and the user clicks outside of its container,
     * this function closes the widget. It relies on the helper function
     * `isClickedOutside` to determine whether the click occurred outside
     * of the chat container element.
     *
     * @param {MouseEvent} event - The click event triggered by the document.
     */
    const outsideClickHandler = (event) => {
      const widgetContainer = document.getElementById("gorgias-chat-container");

      if (
        window.GorgiasChat?.isOpen() &&
        widgetContainer &&
        isClickedOutside(event, widgetContainer)
      ) {
        window?.GorgiasChat.close();
      }
    };

    // Handle closing an open Gorgias chat widget if the user clicks outside the chat's GUI
    window.GorgiasChat?.on("widget:opened", () => {
      // Use `requestIdleCallback` to add listener on next event loop. This avoid conflicts with programmatic widget open() calls.
      requestIdleCallback(() => {
        // Check to make sure the chat window is still open before we add the handler
        if (window.GorgiasChat?.isOpen()) {
          // We can use the `once` option because the widget body is an iframe and won't register a click unless clicked outside
          document.addEventListener("click", outsideClickHandler, {
            once: true,
          });
        }
      });
    });

    // Remove the closing widget handler if the widget is closed from inside gorgias
    window.GorgiasChat?.on("widget:closed", () => {
      document.removeEventListener("click", outsideClickHandler);
    });
  });

  window.addEventListener(
    "aftersell-upcart:public-events:cart-loaded",
    () => {
      requestIdleCallback(() => {
        const originalFetch = window.fetch;

        window.fetch = async (input, init = {}) => {
          const url = typeof input === "string" ? input : input?.url;

          if (url?.includes("/cart/add.js") && init.body) {
            let body;

            if (typeof init.body === "string") {
              try {
                body = JSON.parse(init.body);
              } catch {
                // Suppress errors for non-JSON body data (e.g. form posts)
              }
            }

            if (body?.items?.length) {
              body.items.forEach((item) => {
                if (item.properties?.__upcartRecommendation)
                  delete item.properties.__upcartRecommendation;
                if (item.properties?.__upcartUpsell)
                  delete item.properties.__upcartUpsell;
              });

              init.body = JSON.stringify(body);
            }
          }

          return originalFetch(input, init);
        };
      });
    },
    { once: true },
  );

  if (
    window.Theme?.settings?.isWesperDeferredPaymentsEnabled &&
    window.Theme?.settings?.wesperVariantId &&
    window.Theme?.settings?.sealWesperSellingPlanId
  ) {
    // Intercept fetch requests to add the selling plan ID for the specified variant when the selling plan isn't already included in the request body
    // This is needed to ensure that the correct selling plan is added to cart when a product is added via AJAX (e.g. from a product card) instead of a standard product form submission
    const WESPER_VARIANT_ID = window.Theme.settings.wesperVariantId;
    const SELLING_PLAN_ID = window.Theme.settings.sealWesperSellingPlanId;

    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const [resource, config = {}] = args;

      const url = typeof resource === "string" ? resource : resource?.url;

      if (url?.includes("/cart/add")) {
        const body = config.body;

        // Standard Shopify product forms
        if (body instanceof FormData) {
          const variantId = body.get("id");

          if (
            String(variantId) === WESPER_VARIANT_ID &&
            !body.get("selling_plan")
          ) {
            body.set("selling_plan", SELLING_PLAN_ID);
          }
        } else if (typeof body === "string") {
          // AJAX cart requests
          try {
            const parsedBody = JSON.parse(body);

            if (Array.isArray(parsedBody.items)) {
              parsedBody.items.forEach((item) => {
                if (
                  String(item.id) === WESPER_VARIANT_ID &&
                  !item.selling_plan
                ) {
                  item.selling_plan = SELLING_PLAN_ID;
                }
              });

              config.body = JSON.stringify(parsedBody);
            }

            // Handle single-item cart/add.js payloads too
            else if (
              String(parsedBody.id) === WESPER_VARIANT_ID &&
              !parsedBody.selling_plan
            ) {
              parsedBody.selling_plan = SELLING_PLAN_ID;
              config.body = JSON.stringify(parsedBody);
            }
          } catch {
            // Ignore non-JSON request bodies
          }
        }
      }

      return originalFetch.apply(this, args);
    };
  }
});
